import { Query } from "@apollo/client/react/components";
import React from "react";
import * as palette from "@govlaunch/palette";
import StoryListItemDividers from "~/components/story/StoryListItemDividers";
import StoryListItemHoverContainer from "~/components/story/StoryListItemHoverContainer";
import StoryListItem, { TStoryListItemStory } from "~/components/story/StoryListItem";
import GetStoriesByVendor from "~/lib/queries/GetStoriesByVendor";
import {
  IGetStoriesByVendorQuery,
  IGetStoriesByVendorQueryVariables,
} from "~/lib/queries/__generated__/GetStoriesByVendor.generated";
import NoVendorStoriesBox from "~/components/vendor/NoVendorStoriesBox";
import { LoadingIndicator } from "@govlaunch/core";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import UpgradeAccountBanner from "~/components/vendor/profile/UpgradeAccountBanner";
import { FormattedMessage } from "react-intl";
import { IPlanTier } from "~/types/types";

interface IVendorStoriesProps {
  vendor: NonNullable<IVendorQuery["company"]>;
  showUpgradeBanner?: boolean;
}

interface IShowMoreProps {
  onClick: () => any;
}

export default function VendorStories({ vendor, showUpgradeBanner = true }: IVendorStoriesProps) {
  if (vendor.plan.tier == IPlanTier.Basic && vendor.viewerIsMember) {
    return showUpgradeBanner ? <UpgradeAccountBanner /> : null;
  }

  return (
    <Query<IGetStoriesByVendorQuery, IGetStoriesByVendorQueryVariables>
      query={GetStoriesByVendor}
      variables={{
        vendorId: vendor._id,
        sort: null,
        cursor: null,
        limit: 6,
      }}
      notifyOnNetworkStatusChange={true}
    >
      {({ data, loading, fetchMore }) => {
        if (loading) {
          return <LoadingIndicator />;
        }

        if (!data || !data.storiesByVendor || !data.storiesByVendor.items || data.storiesByVendor.items.length === 0) {
          return <NoVendorStoriesBox />;
        }

        const stories = data.storiesByVendor.items;
        const hasMore =
          data.storiesByVendor.pageInfo && data.storiesByVendor.pageInfo.hasNextPage
            ? data.storiesByVendor.pageInfo.hasNextPage
            : false;

        return (
          <VendorStoriesList
            stories={stories as TStoryListItemStory[]}
            hasMore={hasMore}
            onShowMore={() => {
              if (!hasMore || !data.storiesByVendor.pageInfo || !data.storiesByVendor.pageInfo.endCursor) {
                return null;
              }

              fetchMore({
                variables: {
                  vendorId: vendor._id,
                  sort: null,
                  cursor: data.storiesByVendor.pageInfo.endCursor,
                  limit: 4,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  if (
                    !fetchMoreResult ||
                    !fetchMoreResult.storiesByVendor ||
                    !fetchMoreResult.storiesByVendor.items ||
                    !previousResult.storiesByVendor ||
                    !previousResult.storiesByVendor.items
                  ) {
                    return previousResult;
                  }

                  return {
                    ...previousResult,
                    storiesByVendor: {
                      ...previousResult.storiesByVendor,
                      ...fetchMoreResult.storiesByVendor,
                      items: previousResult.storiesByVendor.items.concat(fetchMoreResult.storiesByVendor.items),
                    },
                  };
                },
              });
            }}
          />
        );
      }}
    </Query>
  );
}

interface IVendorStoriesListProps {
  stories: TStoryListItemStory[];
  hasMore: boolean;
  onShowMore: () => void;
}

function VendorStoriesList({ stories, onShowMore, hasMore }: IVendorStoriesListProps) {
  return (
    <div
      css={{
        backgroundColor: palette.white,
        borderRadius: 5,
        border: `solid 1px ${palette.lightestGray}`,
      }}
      data-intercom-target="Vendor Stories"
    >
      <StoryListItemDividers>
        {stories.map((story) => {
          return (
            <StoryListItemHoverContainer
              key={story._id}
              style={{
                padding: 15,
              }}
            >
              <StoryListItem story={story as TStoryListItemStory} />
            </StoryListItemHoverContainer>
          );
        })}
      </StoryListItemDividers>

      {hasMore && <ShowMoreButon onClick={() => onShowMore()} />}
    </div>
  );
}

function ShowMoreButon(props: IShowMoreProps) {
  return (
    <div
      role="button"
      aria-label="View More"
      css={{
        padding: 15,
        textTransform: "uppercase",
        color: palette.sealBlue,
        fontSize: 12,
        textAlign: "center",
        fontWeight: 500,
        cursor: "pointer",
        borderTop: `solid 1px ${palette.lightestGray}`,
        "&:hover": {
          color: palette.darkGray,
        },
      }}
      {...props}
    >
      <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
    </div>
  );
}
